/**
 * Provenance banner: a one-line, dismissible note that tells the viewer whether the swaths on the
 * map are LIVE radar detections or SYNTHETIC demo data, and that hail-size estimates are
 * approximate (radar physics + the current atmospheric profile; the synthetic demo uses the
 * seasonal isotherm climatology — see api/services/isotherms.py / ADR-001).
 *
 * Today the real radar carries no hail, so the product is shown via the demo seed — without this
 * note a reviewer could mistake the synthetic Skalky swath for a real event. The banner is the one
 * place the UI states that caveat plainly. Pure DOM, no framework; the dismiss state is per-session
 * only (re-shows on reload) so the caveat is never permanently hidden.
 */

/** What the map is currently showing, derived from stored events + live-detection metadata. */
export type Provenance = 'synthetic' | 'live' | 'none';

const MESSAGES: Record<Provenance, string> = {
  // Demo seed populated the map; estimates use the seasonal-climatology isotherms, not real physics.
  synthetic:
    'Ukázková data: zobrazený zásah je syntetické demo (Skalky), ne reálná detekce. ' +
    'Velikost krup je orientační — používá sezónní klimatologii nulové izotermy, ne kalibrovaný profil.',
  // Real detection produced swaths; the size is an estimate, not a ground-truth measurement.
  live: 'Živá detekce z radaru ČHMÚ. Velikost krup je orientační odhad z radarové fyziky.',
  // Nothing detected and nothing seeded — the normal "clear sky" state.
  none: 'Aktuálně žádné krupobití nad ČR.',
};

export class Banner {
  private current: Provenance | null = null;
  private dismissed = false;

  constructor(private readonly root: HTMLElement) {}

  /**
   * Show the note for the given provenance. Re-rendering with the same provenance is a no-op so a
   * periodic refresh does not resurrect a banner the user just dismissed.
   */
  show(provenance: Provenance): void {
    if (provenance === this.current) return;
    this.current = provenance;
    this.dismissed = false;
    this.render();
  }

  private render(): void {
    if (this.current === null || this.dismissed) {
      this.root.innerHTML = '';
      return;
    }
    const tone = this.current === 'synthetic' ? 'banner-synthetic' : 'banner-live';
    this.root.innerHTML = `
      <div class="banner ${tone}" role="status">
        <span class="banner-text">${MESSAGES[this.current]}</span>
        <button type="button" class="banner-dismiss" aria-label="Zavřít">×</button>
      </div>`;
    const dismiss = this.root.querySelector<HTMLButtonElement>('.banner-dismiss');
    dismiss?.addEventListener('click', () => {
      this.dismissed = true;
      this.render();
    });
  }
}
